import type { IConfigurationDetail, IConnectorDetailModel } from 'models/common';
import type { IStationDetailsModel } from 'models/search-page';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import type { IStartChargeProps } from 'types/propTypes';
import { env } from '../../../config/env';
import type { IPaymentInvoiceResponse } from '../../../models/payment';
import { getConfigurationDetail } from '../../../services/common/common-service';
import { createPaymentInvoice } from '../../../services/payment/payment-service';
import { getStationDetailsByCode } from '../../../services/search-page/search-page-service';
import { Common } from '../../../utils/constants';
import { getDNAPaymentScript, getPublicConfigurationValue } from '../../../utils/payment';

export const useStartCharge = (props: IStartChargeProps) => {
    const { t } = props;

    const [stationDetails, setStationDetails] = useState<IStationDetailsModel | null>(null);
    const [connectorDetails, setConnectorDetails] = useState<IConnectorDetailModel | null>(null);
    const [preAuthAmount, setPreAuthAmount] = useState<number>(0);
    const [tncLink, setTncLink] = useState<string>('');
    const [allowCharity, setAllowCharity] = useState<boolean>(false);
    const [charityText, setCharityText] = useState<string>('');
    const [isCharityTextTruncate, setIsCharityTextTruncate] = useState<boolean>(false);
    const [charityLink, setCharityLink] = useState<string>('');
    const [charityImage, setCharityImage] = useState<string>('');
    const [donationType, setDonationType] = useState<string>('');
    const [donationNote, setDonationNote] = useState<string>('');
    const [donationValue, setDonationValue] = useState<number>(0);
    const [showCharityModal, setShowCharityModal] = useState<boolean>(false);
    const [charityAmount, setCharityAmount] = useState<number>(0);
    const [email, setEmail] = useState<string>('');

    const location = useLocation();
    const { code, connector } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (
            connector &&
            +connector > 0 &&
            stationDetails &&
            stationDetails.connectors &&
            stationDetails.connectors.length > 0
        ) {
            const filteredConnector = stationDetails.connectors.filter((con) => con.id == +connector);
            if (filteredConnector.length > 0) {
                setConnectorDetails(filteredConnector[0]);
            }
        }
    }, [stationDetails]);

    useEffect(() => {
        getPreAuthAmount();
        getTnc();

        const getConfigData = async () => {
            let charityDonationConfig: IConfigurationDetail[] = [];
            await getConfigurationDetail({})
                .then(async (res) => {
                    charityDonationConfig = res.Data;
                    if (charityDonationConfig.length > 0) {
                        let isCharityDonation = false;
                        let charityImage = '';
                        let charityLink = '';
                        let isCharityTextTruncate = false;
                        let truncatedCharityText = '';
                        let donationType = '';
                        let donationValue: number | null = 0;
                        let donationNote = '';

                        const charityConfig = charityDonationConfig.find(
                            (x) => x.reference_code === Common.ConfigurationKey.CHARITY_DONATION,
                        );
                        const charityDonation = charityConfig.value ? charityConfig.value : '';
                        if (charityDonation.trim().toLowerCase() === 'false') {
                            isCharityDonation = false;
                        } else {
                            isCharityDonation = true;
                        }
                        if (isCharityDonation) {
                            const charityImageConfig = charityDonationConfig.find(
                                (x) => x.reference_code === Common.ConfigurationKey.CHARITY_DONATION_IMAGE,
                            );
                            charityImage =
                                charityImageConfig && charityImageConfig.value ? charityImageConfig.value : '';

                            const charityLinkConfig = charityDonationConfig.find(
                                (x) => x.reference_code === Common.ConfigurationKey.CHARITY_DONATION_LINK,
                            );
                            charityLink = charityLinkConfig && charityLinkConfig.value ? charityLinkConfig.value : '';

                            const charityTextConfig = charityDonationConfig.find(
                                (x) => x.reference_code === Common.ConfigurationKey.CHARITY_DONATION_TEXT,
                            );
                            truncatedCharityText =
                                charityTextConfig && charityTextConfig.value ? charityTextConfig.value : '';
                            if (truncatedCharityText.length > 500) {
                                truncatedCharityText = truncatedCharityText.slice(0, 500);
                                truncatedCharityText = `${truncatedCharityText}...`;
                                isCharityTextTruncate = true;
                            }

                            const donationTypeConfig = charityDonationConfig.find(
                                (x) => x.reference_code === Common.ConfigurationKey.DONATION_TOPUP_OR_ROUNDUP,
                            );
                            donationType = donationTypeConfig?.value;

                            const donationValueConfig = charityDonationConfig.find(
                                (x) => x.reference_code === Common.ConfigurationKey.DONATION_VALUE,
                            );
                            donationValue = Number(Number(donationValueConfig?.value).toFixed(2));

                            const donationNoteConfig = charityDonationConfig.find(
                                (x) => x.reference_code === Common.ConfigurationKey.WEB_PAY_DONATION_NOTE,
                            );
                            donationNote = donationNoteConfig?.value;
                        }

                        setAllowCharity(isCharityDonation);
                        setDonationType(donationType);
                        setDonationValue(donationValue);
                        setCharityImage(charityImage);
                        setCharityText(truncatedCharityText);
                        setIsCharityTextTruncate(isCharityTextTruncate);
                        setCharityLink(charityLink);
                        setDonationNote(donationNote);
                    }
                })
                .catch((m) => {
                    toast.error(m.toString());
                });
        };

        getConfigData();

        if (code && code.length > 10) {
            toast.error(t('stationDetails.error.invalidCode').toString());
            navigate('/');
        } else {
            if (location.state) {
                setStationDetails(location.state);
            } else {
                setStationDetailsByCode(code);
            }
        }
    }, []);

    const getPreAuthAmount = async () => {
        await getConfigurationDetail({
            code: Common.ConfigurationKey.WEB_PAY_PRE_AUTH_AMOUNT,
        })
            .then((res) => {
                setPreAuthAmount(parseInt(res.Data[0]?.value));
            })
            .catch((e) => {
                console.error('error', e);
            });
    };

    const getTnc = async () => {
        await getConfigurationDetail({
            code: Common.ConfigurationKey.WEB_PAY_TERM_AND_CONDITIONS,
        })
            .then((res) => {
                setTncLink(res.Data[0]?.value);
            })
            .catch((e) => {
                console.error('error', e);
            });
    };

    const setStationDetailsByCode = async (code) => {
        await getStationDetailsByCode(code)
            .then((res) => {
                if (res.Data && Object.keys(res.Data).length !== 0) {
                    setStationDetails(res.Data);
                } else {
                    toast.error(t('stationDetails.error.stationNotFound').toString());
                    navigate('/');
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
                navigate('/');
            });
    };

    const handleSubmit = async (email: string) => {
        let charityAmount = 0;
        setEmail(email);
        if (allowCharity) {
            if (donationType && donationType.toUpperCase() === Common.DonationType.TOPUP) {
                charityAmount = Number(donationValue?.toFixed(2));
            }

            setShowCharityModal(true);
            setCharityAmount(charityAmount);
        } else {
            doPayment(email, false);
        }
    };

    const hideCharityModal = () => {
        setShowCharityModal(false);
    };

    const doPayment = async (email: string, includeCharity: boolean) => {
        const enable_google_apple_pay = await getPublicConfigurationValue(
            Common.ConfigurationKey.ENABLE_GOOGLE_PAY_APPLE_PAY_DNA,
        );
        if (connector) {
            const inputData = {
                connector_id: Number(connector),
                email,
                include_charity: includeCharity,
            };
            const paymentInvoice = await generatePaymentInvoice(inputData);
            if (paymentInvoice) {
                document.getElementById('loading').classList.remove('spinner-disable');
                const isTestMode = !!(
                    paymentInvoice.isTestMode === undefined ||
                    paymentInvoice.isTestMode === null ||
                    paymentInvoice.isTestMode === true
                );
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = getDNAPaymentScript(isTestMode);
                script.nonce = 'ils+vsV+OAN1z2QUGBzoyQ==';
                script.async = true;
                script.onload = () => {
                    window.DNAPayments.configure({
                        autoRedirectDelayInMs: 3000,
                        scopes: {
                            allowHosted: true,
                        },
                        paymentMethods: [
                            {
                                name: window.DNAPayments.paymentMethods.BankCard,
                            },
                            ...(enable_google_apple_pay === 'true'
                                ? [
                                      {
                                          name: window.DNAPayments.paymentMethods.GooglePay,
                                      },
                                      {
                                          name: window.DNAPayments.paymentMethods.ApplePay,
                                      },
                                  ]
                                : []),
                        ],
                        isTestMode: isTestMode,
                        paymentTimeoutInSeconds: 900,
                    });

                    const invoiceId = paymentInvoice.invoice_id;

                    const paymentData = {
                        paymentSettings: {
                            terminalId: paymentInvoice.terminal,
                            returnUrl: `${window.location.origin}/portal/charge-station/${code}/${connector}/payment/success/${invoiceId}`,
                            failureReturnUrl: `${window.location.origin}/portal/charge-station/${code}/${connector}/payment/fail/${invoiceId}`,
                            callbackUrl: `${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/web-pay/payment/dna-webhook/${invoiceId}`,
                            failureCallbackUrl: `${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/web-pay/payment/dna-webhook/${invoiceId}`,
                            cscMatrix: 22,
                            paMatrix: 3,
                        },
                        customerDetails: {
                            accountDetails: {
                                accountId: invoiceId,
                            },
                        },
                        periodic: {
                            periodicType: 'ucof',
                        },
                        invoiceId: invoiceId,
                        amount: +paymentInvoice.amount,
                        auth: { access_token: paymentInvoice.access_token },
                        language: 'en',
                        description: 'Pre auth to InstaVolt',
                        transactionType: paymentInvoice.transaction_type || 'PRE-AUTH',
                        currency: paymentInvoice.currency,
                    };
                    localStorage.setItem('email', email);
                    window.DNAPayments.openPaymentPage(paymentData);
                };

                script.onerror = () => {
                    document.getElementById('loading').classList.add('spinner-disable');
                    toast.error(t('common.errors.somethingWentWrong').toString());
                };
                document.body.appendChild(script);
            }
        }
    };

    const generatePaymentInvoice = async (inputData) => {
        let paymentInvoice: IPaymentInvoiceResponse;
        await createPaymentInvoice(+inputData.connector_id, inputData.email, inputData.include_charity)
            .then(async (res) => {
                if (res.Data) {
                    paymentInvoice = res.Data;
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
        return paymentInvoice;
    };

    return {
        t,
        stationDetails,
        connectorDetails,
        preAuthAmount,
        tncLink,
        allowCharity,
        charityText,
        isCharityTextTruncate,
        charityLink,
        charityImage,
        donationType,
        donationNote,
        donationValue,
        showCharityModal,
        charityAmount,
        email,
        setAllowCharity,
        setDonationType,
        getPreAuthAmount,
        getTnc,
        setStationDetailsByCode,
        handleSubmit,
        hideCharityModal,
        doPayment,
        generatePaymentInvoice,
    };
};
